import PageLayout from "@/components/PageLayout";
import PageHeader from "@/components/PageHeader";
import { motion } from "framer-motion";
import { useEffect, useRef } from "react";
import { AlertCircle } from "lucide-react";
import SEO from "@/components/SEO";

const plans = [
  {
    name: "Aikuiset",
    subtitle: "",
    price: "39,90",
    period: "/kk",
    desc: "Toistaiseksi voimassa oleva sopimus. Kuukausilaskutus. Kalenterikuukauden irtisanomisaika. Kaikki liikuntaedut käyvät maksuvälineenä.",
    extras: [
      "10x kortti 69,00€ (12kk)",
      "Viikkokortti 18,00€",
      "1 irtokuukausi 47,00€",
      "3 irtokuukautta 132,00€",
      "Vuosikortti 480,00€",
      "Kertamaksu 9,00€",
    ],
    featured: false,
  },
  {
    name: "Opiskelija / Eläkeläinen",
    subtitle: "Opiskelija, työtön, eläkeläinen, hoitovapaa, varusmies",
    price: "29,90",
    period: "/kk",
    desc: "Toistaiseksi voimassa oleva sopimus. Kuukausilaskutus. Kalenterikuukauden irtisanomisaika.",
    extras: [
      "10x kortti 59,00€ (12kk)",
      "Viikkokortti 18,00€",
      "1 irtokuukausi 37,00€",
      "3 irtokuukautta 100,00€",
      "Vuosikortti 360,00€",
      "Kertamaksu 7,00€",
    ],
    featured: false,
  },
  {
    name: "Muut palvelut",
    subtitle: "Ohjelmat ja kulkukortti",
    price: "",
    period: "",
    desc: "",
    extras: [
      "1-jakoinen ohjelma + ohjaus 49,00€",
      "2-jakoinen ohjelma + ohjaus 55,00€",
      "3-jakoinen ohjelma + ohjaus 59,00€",
      "Kulkukortti 8,00€",
    ],
    featured: false,
  },
];

const paymentMethods = [
  "Edenred", "Epassi", "Smartum", "Tyky", "Virikeseteli",
  "Ticket Mind & Body", "MobilePay", "Maksukortit", "Käteinen",
];

export default function HinnastoPage() {
  const webstoreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://avoinna24.fi/js/a24.iframe.js";
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  // Handle hash scroll on mount
  useEffect(() => {
    if (window.location.hash === "#webstore") {
      setTimeout(() => {
        webstoreRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 500);
    }
  }, []);

  const scrollToWebstore = () => {
    webstoreRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <PageLayout>
      <SEO title="Hinnasto — Kuntosali Takomo" description="Kuntosali Takomon jäsenyydet ja hinnat. Aikuiset, opiskelijat, 10x-kortit ja viikkokortit. Jokela ja Kellokoski — molempiin kulku samalla kortilla." path="/hinnasto" />
      <PageHeader title="Hinnasto" />

      <section className="section-padding">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {plans.map((plan, i) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-card-premium rounded-lg overflow-hidden flex flex-col"
              >
                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="font-display text-xl font-semibold text-foreground mb-1 text-center">{plan.name}</h3>
                  {plan.subtitle && <p className="text-muted-foreground text-xs mb-4 text-center">{plan.subtitle}</p>}

                  {plan.price && (
                    <div className="mb-4 text-center">
                      <span className="text-5xl font-display font-bold text-primary">{plan.price}€</span>
                      <span className="text-muted-foreground text-sm">{plan.period}</span>
                    </div>
                  )}

                  {plan.desc && <p className="text-muted-foreground text-sm mb-6 leading-relaxed">{plan.desc}</p>}

                  <ul className="space-y-2 mb-4 flex-1">
                    {plan.extras.map((e) => (
                      <li key={e} className="text-sm text-foreground/80 flex items-start gap-2">
                        <span className="text-primary mt-1">•</span>
                        {e}
                      </li>
                    ))}
                  </ul>

                  {plan.name === "Opiskelija / Eläkeläinen" && (
                    <div className="mb-6 flex items-start gap-2 rounded border border-border bg-secondary/40 p-3 text-xs text-muted-foreground leading-relaxed">
                      <AlertCircle className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
                      <span>
                        Yläastelaisilla salille pääsy estetty Ma–Pe klo 15.00–19:30.{" "}
                        <a
                          href="#ylaastelaiset"
                          onClick={(e) => {
                            e.preventDefault();
                            document.getElementById("ylaastelaiset")?.scrollIntoView({ behavior: "smooth" });
                          }}
                          className="text-primary hover:underline"
                        >
                          Lue lisää
                        </a>
                      </span>
                    </div>
                  )}


                  {plan.name === "Opiskelija / Eläkeläinen" ? (
                    <div
                      aria-disabled="true"
                      className="flex items-center justify-center gap-2 py-3 rounded font-display text-sm tracking-wider uppercase btn-outline-premium w-full pointer-events-none select-none"
                    >
                      Ostettava asiakaspalvelusta
                    </div>
                  ) : (
                    <button
                      onClick={scrollToWebstore}
                      className="flex items-center justify-center gap-2 py-3 rounded font-display text-sm tracking-wider uppercase transition-all btn-outline-premium w-full cursor-pointer"
                    >
                      Osta
                    </button>
                  )}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Info Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 max-w-3xl mx-auto space-y-10"
          >
            <div>
              <h3 className="font-display text-xl font-semibold text-foreground mb-3">Toistaiseksi voimassa oleva jäsenyys</h3>
              <ul className="space-y-2 text-sm text-muted-foreground leading-relaxed">
                <li>Irtisanomisaika aina ilmoituksesta seuraavan kuun loppuun.</li>
                <li>Voit maksaa myös kaikilla suomalaisilla liikuntaetuuksilla.</li>
                <li>Laskun saa sähköpostiin tai e-laskuna.</li>
                <li>Kortin saa irtisanottua sähköpostilla, <a href="mailto:info@kuntotakomo.fi" className="text-primary hover:underline">info@kuntotakomo.fi</a></li>
                <li>Korttia voi käyttää salin aukioloaikoina, Jokela 24/7, Kellokoski 05.00–22.00.</li>
              </ul>
            </div>

            <div>
              <h3 className="font-display text-xl font-semibold text-foreground mb-3">Irtokuukaudet</h3>
              <ul className="space-y-2 text-sm text-muted-foreground leading-relaxed">
                <li>Irtokuukausi ladataan voimaan haluamastasi päivästä ja se on siitä kuukauden eteenpäin voimassa.</li>
                <li>Voit ladata kerrallaan 1–12 kk.</li>
                <li>Kortti lakkaa toimimasta kun aika on käytetty loppuun ja voit halutessasi ladata lisää aikaa.</li>
                <li>Voit maksaa myös kaikilla suomalaisilla liikuntaetuuksilla.</li>
                <li>Korttia voi käyttää salin aukioloaikoina, Jokela 24/7, Kellokoski 05.00–22.00.</li>
              </ul>
            </div>

            <div>
              <h3 className="font-display text-xl font-semibold text-foreground mb-3">10x kortti ja kertamaksut</h3>
              <ul className="space-y-2 text-sm text-muted-foreground leading-relaxed">
                <li>10x kortti on voimassa vuoden ostopäivästä.</li>
                <li>Kun kortti on käytetty loppuun, voit halutessasi ostaa sinne lisää käyntejä.</li>
                <li>10x korttia voi käyttää salin aukioloaikoina, Jokela 24/7, Kellokoski 05.00–22.00.</li>
                <li className="mt-4">Kertamaksulla kuntosaleille vain päivystysaikoina.</li>
                <li>Voit maksaa myös kaikilla suomalaisilla liikuntaetuuksilla.</li>
              </ul>
            </div>

            <div id="ylaastelaiset" className="scroll-mt-24">
              <h3 className="font-display text-xl font-semibold text-foreground mb-3">Yläastelaisten aikarajoitukset</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Ma–Pe klo 15.00–19:30 yläastelainen ei pääse kortilla sisään. La & Su pääsy normaalisti. Vielä klo 14:59 pääsee sisälle ja voi treenata normaalisti. Kesäaikana 1.6.–10.8. tämä ei käytössä.
              </p>
            </div>

          </motion.div>

          {/* Payment Methods */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mt-16 text-center"
          >
            <h3 className="font-display text-xl mb-4 text-foreground">Maksuvälineet</h3>
            <div className="flex flex-wrap justify-center gap-3">
              {paymentMethods.map((m) => (
                <span key={m} className="bg-secondary text-muted-foreground text-xs px-3 py-1.5 rounded border border-border">
                  {m}
                </span>
              ))}
            </div>
            <p className="text-muted-foreground text-xs mt-4">MobilePay: Takomogym / 49878</p>
          </motion.div>

          {/* Embedded Webstore */}
          <div ref={webstoreRef} id="webstore" className="mt-16">
            <h3 className="font-display text-xl mb-6 text-foreground text-center">Liity jäseneksi</h3>
            <div className="bg-gray-100 rounded-lg p-4">
              <iframe
                src="https://avoinna24.fi/takomo/join/member"
                allowTransparency={true}
                className="a24-iframe"
                frameBorder="0"
                scrolling="no"
                style={{ width: "100%", minHeight: "800px" }}
              />
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
